<template>
  <div class="contain">
    <!-- 검색 영역 -->
    <div class="search_form" ref="search_form">
      <el-form
        :inline="true"
        :label-position="$device.mobile ? `top` : `left`"
        label-suffix=":"
        :size="size"
        @submit.native.prevent>
        <el-row>
          <el-col :md="8">
            <el-form-item :label="$t('대상 매장')">
              <span class="dm-scope">{{ scopeStoreName(storeCd) }}</span>
            </el-form-item>
          </el-col>
          <el-col :md="8">
            <el-form-item :label="$t('형제 매장 포함')">
              <el-switch v-model="group" @change="search"></el-switch>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <!-- 버튼 -->
    <div class="buttons" ref="buttons">
      <el-button :size="size" @click="search" icon="el-icon-search">{{ $t('검색') }}</el-button>
    </div>

    <el-alert
      class="dm-hint"
      type="info"
      :closable="false"
      show-icon
      :title="$t('오더엔 값과 배달앱 값이 다릅니다. 어느 쪽이 정본인지 고르기 전까지 이 메뉴의 반영이 멈춥니다.')">
    </el-alert>

    <!-- 리스트 -->
    <el-table
      :height="tableHeight"
      ref="table"
      border
      stripe
      tooltip-effect="dark"
      :data="tableData"
      :size="size"
      :empty-text="$t('확인이 필요한 차이가 없습니다.')">
      <el-table-column
        :label="tableData.length.toString()"
        type="index"
        width="55"
        align="center"
        label-class-name="data_cnt"></el-table-column>
      <el-table-column :label="$t('메뉴')" min-width="150" show-overflow-tooltip>
        <template slot-scope="s">
          {{ s.row.smenuNm }}
          <el-tag v-if="isRequested(s.row)" size="mini" type="info">{{ $t('반영 요청됨') }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="$t('플랫폼')" width="90">
        <template slot-scope="s">{{ platformName(s.row.platform) }}</template>
      </el-table-column>
      <!--
        **`이름(번호)` 한 줄이다** (§16.179). 이 화면에서 특히 그렇다: 배민 `PRINCIPAL` 은 값이
        어긋난 샵이 **다른 매장의 것**일 수 있어(§16.113) 번호만으로는 "내 가게가 아닌 어딘가"로만
        읽힌다. 아래 줄의 매장명은 다른 축(오더엔 매장)이라 그대로 둔다.
      -->
      <el-table-column :label="$t('가게')" width="180" show-overflow-tooltip>
        <template slot-scope="s">
          {{ shop(s.row) }}
          <div v-if="isOtherStore(s.row)" class="dm-weak">{{ s.row.storeNm || scopeStoreName(s.row.storeCd) }}</div>
        </template>
      </el-table-column>
      <!--
        ★★ **오더엔 쪽을 그릴 수 없으면 그리지 않고 말한다** (`DRIFT-VIEW-BASIS-MISMATCH`).
        전파로 딸려온 행은 판정이 **그 링크 자신의 메뉴**로 나는데 행은 **답할 메뉴**로 그려진다.
        두 기준이 다르면 화면이 판정을 설명하지 못해, 글자 그대로 같은 두 값 옆에 「반영 차단」이
        붙는다(dev 링크 653). **문구도 판정도 서버가 답한다** — 화면이 조건을 다시 적으면
        축이 늘 때 두 곳이 갈린다.
      -->
      <el-table-column :label="$t('다른 항목 (반영 차단)')" min-width="300">
        <template slot-scope="s">
          <div v-for="f in s.row.fields" :key="'b-' + f.field" class="dm-field">
            <span class="dm-field__name">{{ fieldLabel(f.field) }}</span>
            <span v-if="s.row.ordernHiddenReason" class="dm-field__ordern dm-weak">-</span>
            <span v-else class="dm-field__ordern">{{ valueText(f.ordern) }}</span>
            <i class="el-icon-right"></i>
            <span class="dm-field__platform">{{ valueText(f.platform) }}</span>
          </div>
          <div v-if="s.row.ordernHiddenReason" class="dm-field__note">
            {{ s.row.ordernHiddenReason }}
          </div>
          <span v-if="!s.row.fields || s.row.fields.length === 0" class="dm-weak">-</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('참고 (차단 아님)')" min-width="260">
        <template slot-scope="s">
          <div v-for="f in s.row.unsupportedDiffFields" :key="'u-' + f.field" class="dm-field dm-field--weak">
            <span class="dm-field__name">{{ fieldLabel(f.field) }}</span>
            <span>{{ valueText(f.ordern) }}</span>
            <i class="el-icon-right"></i>
            <span>{{ valueText(f.platform) }}</span>
            <!--
              **포장 축에는 조건을 붙인다** (§16.171). 2026-08-20 개통으로 이 축도 배달앱까지 가지만
              **메뉴마다 갈린다** — 배민이 `매장가 ≥ 포장가` 를 강제하므로 매장가격이 선 메뉴는
              나가고 없는 메뉴는 룰이 닫는다. 그 조건을 말하지 않으면 사장님은 값을 맞추려고
              배달앱을 고치게 되고, 그러면 이번엔 배달가가 어긋난다.
              **사용 여부 축에는 그 플랫폼의 숨김 범위를 붙인다** (§16.302) — 이 열은 capability 가
              닫힌 축이라 「숨깁니다」를 단정하지 않고, 행의 플랫폼 문장만 붙는다.
            -->
            <div v-if="fieldNote(f.field, s.row.platform)" class="dm-field__note">
              {{ fieldNote(f.field, s.row.platform) }}
            </div>
          </div>
          <span v-if="!s.row.unsupportedDiffFields || s.row.unsupportedDiffFields.length === 0" class="dm-weak">-</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('관측 시각')" width="150">
        <!--
          ★ 관측 나이를 함께 말한다 (`OPTION-OBSERVE-LAG`) — 배달앱에서 방금 바꾼 직후 밀면
          옛 관측으로 지목해 실패한다. 임계값은 적지 않는다: 관측 주기는 실행자가 소유하므로
          「N분이면 낡음」을 여기 적으면 상수가 두 벌이 된다.
        -->
        <template slot-scope="s">
          <el-tooltip v-if="observationAgeNote(s.row.observedAt)" placement="top">
            <div slot="content" style="max-width: 320px">{{ observationAgeNote(s.row.observedAt) }}</div>
            <span class="dm-observed">{{ formatDate(s.row.observedAt) }}</span>
          </el-tooltip>
          <span v-else>{{ formatDate(s.row.observedAt) }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('정본 선택')" width="220" align="center">
        <template slot-scope="s">
          <el-button type="text" size="mini" :disabled="!canBind" @click="resolve(s.row, 'PUSH_ORDERN')">
            {{ $t('오더엔 값 반영') }}
          </el-button>
          <el-divider direction="vertical"></el-divider>
          <!--
            ★★ **눌러 봐야 아는 버튼을 없앤다** (`DRIFT-ACCEPT-BUTTON-UNGATED`). 서버는 이 묶음을
            받을 수 없다는 것을 **미리** 알고 `acceptBlockedReason` 으로 답한다 — 종전에는 그 사실이
            목록에 없어 눌러야 400 과 함께 이유를 봤다. 툴팁 문구도 서버 것을 그대로 쓴다.
          -->
          <el-tooltip :disabled="!s.row.acceptBlockedReason" :content="s.row.acceptBlockedReason || ''" placement="top">
            <span>
              <el-button
                type="text"
                size="mini"
                :disabled="!canBind || !!s.row.acceptBlockedReason"
                @click="confirmAccept(s.row)">
                {{ $t('배달앱 값 받기') }}
              </el-button>
            </span>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!-- 결과 -->
    <el-dialog :title="$t('처리 결과')" :visible.sync="resultVisible" width="560px">
      <div v-if="result">
        <p>{{ resultSummary }}</p>
        <div v-if="result.affectedStores && result.affectedStores.length">
          <div class="dm-result__label">{{ $t('함께 바뀐 매장') }}</div>
          <el-tag v-for="st in result.affectedStores" :key="st.storeCd" size="small" class="dm-candidate">
            {{ st.storeNm || scopeStoreName(st.storeCd) }}
          </el-tag>
        </div>
        <!--
          요청을 만들었다는 것과 그것이 실행될 수 있다는 것은 다른 사실이다 (§16.168).
          만들어진 item 이 전부 막히면(오더엔에서 내린 메뉴 등) 반영은 영영 나가지 않는데,
          "요청 N건"만 보여 주면 사장님은 그것을 알 수 없다.
        -->
        <el-alert
          v-if="result.resolution === 'PUSH_ORDERN' && result.pendingItemCount === 0"
          class="dm-hint"
          type="warning"
          :closable="false"
          :title="
            $t(
              '지금은 반영을 시작할 수 없습니다. 메뉴가 사용 안 함이거나 연결 상태가 나빠 대기 중인 작업이 없습니다 — 메뉴 연결 화면에서 상태를 확인해 주세요.'
            )
          "></el-alert>
        <el-alert
          v-else-if="result.resolution === 'PUSH_ORDERN'"
          class="dm-hint"
          type="info"
          :closable="false"
          :title="$t('배달앱에 실제로 적용되면 이 목록에서 사라집니다. 그때까지 행은 남아 있습니다.')"></el-alert>
        <!--
          **가격만 갈린 결정은 오더엔 메뉴 행을 한 줄도 고치지 않는다** (§16.171). 그것을 말하지
          않으면 `updatedSmenuCds` 가 비어 있는 정상 응답이 "아무것도 안 바뀌었다"로 읽힌다.
        -->
        <el-alert v-if="priceNote" class="dm-hint" type="info" :closable="false" :title="priceNote"></el-alert>
      </div>
      <div slot="footer">
        <el-button size="small" type="primary" @click="resultVisible = false">{{ $t('확인') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import deliveryMenuScopeMixin from '@/mixins/deliveryMenuScopeMixin'
import { util } from '@/mixins/utilMixin'
import { initData } from '@/utils/constants'
import {
  acceptPlatformConfirmText,
  DELIVERY_MENU_ACTIONS,
  DELIVERY_MENU_API,
  deliveryFieldLabel,
  deliveryMenuErrorMessage,
  deliveryMenuPlatformName,
  deliveryMenuTimeText,
  deliveryMenuVisibleValue,
  DRIFT_RESOLUTION,
  driftResolveSummary,
  shopText,
} from '@/utils/deliveryMenuSync'
import { deliveryFieldNote, deliveryObservationAgeNote } from '@/utils/deliveryMenuNotes'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'

/**
 * ④ 드리프트 해결 — `GET /drifts` · `POST /drift/resolve`.
 *
 * **행의 축은 (메뉴 행, 링크)다** (§16.113) — 같은 배민 CatalogMenu 를 참조하는 다른 매장의 링크가
 * 이 메뉴의 문제로 보이는 것이 정상이다. 반면 **결정의 축은 `{smenuCd, platform}`** 이라 한 번의
 * 선택이 여러 행을 대상으로 삼는다.
 *
 * 지키는 것
 *  - `PUSH_ORDERN` 뒤에도 행을 지우지 않는다 (§16.62). 반영이 실제로 나갈 때까지 남는 것이 정상이고
 *    같은 버튼을 다시 눌러도 안전하므로 잠그지 않는다.
 *  - `targetLinkIds` 는 "해결된 링크"가 아니라 "이 결정이 대상으로 삼은 링크"다 (§16.62).
 *  - 400 문구는 서버가 다음 행동까지 담아 만든 문장이라 그대로 노출한다 (§16.57·§16.121·§16.134).
 *  - `ORPHAN` 링크는 여기 오지 않는다 (§16.133) — 그 자리는 ③이다.
 */
export default {
  name: 'DeliveryMenuDriftView',
  mixins: [mainVueMixin, deliveryMenuScopeMixin],

  data() {
    const data = initData()
    data.group = false
    data.resultVisible = false
    data.result = null
    /** 이번 화면에서 `PUSH_ORDERN` 을 누른 `smenuCd|platform` — 행에 "반영 요청됨" 배지를 단다 */
    data.requested = []
    return data
  },

  computed: {
    storeCd() {
      return this.scopeStoreCd
    },
    canBind() {
      return checkAuthActionAccess(DELIVERY_MENU_ACTIONS.BIND)
    },
    resultSummary() {
      // 두 갈래가 세는 사실이 다르다 (§16.168·§16.171) — 그 대응은 utils/deliveryMenuSync 가 소유한다
      return driftResolveSummary(this.result)
    },
    /**
     * **채널 가격이 어디에 저장됐는가** (§16.171).
     *
     * 배민은 같은 메뉴에 배달가·포장가를 따로 갖고, 관측이 링크마다 갈리면 각 링크가 자기 값을
     * 받는다. 그때 오더엔 메뉴 행은 바뀌지 않으므로 `updatedSmenuCds` 만 읽는 화면은 정상 해결을
     * "변화 없음"으로 말하게 된다.
     */
    priceNote() {
      const r = this.result
      if (!r || r.resolution === DRIFT_RESOLUTION.PUSH_ORDERN) return ''
      if ((r.updatedPriceLinkIds || []).length === 0) return ''
      return (r.updatedSmenuCds || []).length === 0
        ? this.$t('가격이 배달앱 연결마다 달라 오더엔 메뉴 가격은 그대로 두고 각 연결에 그 값을 저장했습니다.')
        : this.$t('배달앱 연결마다 다른 가격은 그 연결에 따로 저장했습니다.')
    },
  },

  async created() {
    await this.loadScopeStore()
    this.search()
  },

  mounted() {
    this.$nextTick(() => {
      this.mainVue.currVue = this
    })
  },

  methods: {
    platformName: deliveryMenuPlatformName,
    fieldLabel: deliveryFieldLabel,
    fieldNote: deliveryFieldNote,
    observationAgeNote: deliveryObservationAgeNote,
    /** 샵 표기 규칙은 utils/deliveryMenuSync 한 곳이 소유한다 (§16.179) */
    shop: shopText,

    formatDate(value) {
      return deliveryMenuTimeText(value, util.getDate)
    },

    valueText(value) {
      if (value == null) return this.$t('(없음)')
      if (value === '') return this.$t('(빈 값)')
      // 후행 공백 하나로도 드리프트가 선다(§16.122) — 보이지 않으면 고를 수 없다 (§16.169)
      return deliveryMenuVisibleValue(value)
    },

    isOtherStore(row) {
      return Number(row.storeCd) !== Number(this.storeCd)
    },

    isRequested(row) {
      return this.requested.indexOf(`${row.smenuCd}|${row.platform}`) >= 0
    },

    async search() {
      if (this.storeCd == null) return
      try {
        const res = await this.$axios.get(`${DELIVERY_MENU_API}/drifts`, {
          params: { storeCd: this.storeCd, group: this.group },
        })
        this.tableData = Array.isArray(res.data) ? res.data : []
        // 매장명의 출처는 이 응답이다 (규약 §16.152)
        this.rememberStoreNames(this.tableData)
      } catch (e) {
        this.tableData = []
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e) })
      }
    },

    /**
     * `ACCEPT_PLATFORM` 은 이름·설명이 원본 축이라 형제 매장 사본까지 함께 바뀐다 (§11.2).
     * 미리보기 API 가 없으므로 확정 전에 경고하고, 실제 매장 목록은 응답의 `affectedStores` 로 보여준다.
     *
     * 가격 축은 그 경고와 뜻이 반대다 — **갈려도 정상이고 각 연결이 자기 값을 받는다**(§16.171).
     * 그래서 문구는 그 행에 걸린 축을 보고 조립한다(`acceptPlatformConfirmText`).
     */
    confirmAccept(row) {
      this.mainVue
        .$confirm(
          // 조립한 문장이라 i18n 키가 될 수 없다 — chip 문구와 같이 utils 가 만든 것을 그대로 쓴다
          acceptPlatformConfirmText(row),
          this.mainVue.appMsg.systemAlterTitle,
          { confirmButtonText: this.$t('반영'), cancelButtonText: this.$t('취소'), type: 'warning' }
        )
        .then(() => this.resolve(row, DRIFT_RESOLUTION.ACCEPT_PLATFORM))
        .catch(() => {})
    },

    async resolve(row, resolution) {
      try {
        const res = await this.$axios.post(`${DELIVERY_MENU_API}/drift/resolve`, {
          smenuCd: row.smenuCd,
          platform: row.platform,
          resolution,
        })
        this.result = res.data
        this.resultVisible = true
        if (resolution === DRIFT_RESOLUTION.PUSH_ORDERN) {
          // 행은 남는다 — 낙관적으로 지우지 않고 요청됐다는 사실만 표시한다
          const key = `${row.smenuCd}|${row.platform}`
          if (this.requested.indexOf(key) < 0) this.requested.push(key)
        }
        this.search()
      } catch (e) {
        // "이 드리프트는 …할 수 없습니다" 처럼 다음 행동을 담은 문장이라 프론트 문구로 갈아끼우지 않는다
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e), duration: 8000 })
      }
    },
  },
}
</script>

<style scoped>
.dm-scope {
  font-weight: 600;
}
.dm-hint {
  margin-bottom: 8px;
}
.dm-weak {
  color: #909399;
  font-size: 12px;
}
.dm-field {
  line-height: 1.7;
}
.dm-field--weak {
  color: #909399;
  font-size: 12px;
}
.dm-field__note {
  color: #909399;
  font-size: 12px;
  padding-left: 72px;
}
.dm-field__name {
  display: inline-block;
  min-width: 72px;
  font-weight: 600;
}
.dm-field__ordern {
  color: #303133;
}
.dm-field__platform {
  color: #e6a23c;
}
.dm-result__label {
  font-weight: 600;
  margin: 10px 0 6px;
}
.dm-candidate {
  margin: 1px 4px 1px 0;
}
</style>
